import React from 'react';
import { fmtEta, fmtLastSeen, ringColor, dominantState } from './chargeUi';
import RollupChips from './RollupChips';
import ChargeRing from './ChargeRing';
import Tag from '../components/ui/Tag';
import Reveal from '../components/ui/Reveal';
import type { TagTone } from '../components/ui/Tag';
import type { ChargerView } from './types';

function dominantAnim(charger: ChargerView): string {
  if (!charger.online) return '';
  const st = dominantState(charger);
  if (st === 'charging') return 'cc-anim-charge';
  if (st === 'discharging') return 'cc-anim-discharge';
  return '';
}

// Asset-side lifecycle/service signals (from the DB, not the collector) so the
// live monitoring card also flags gear that needs attention.
function assetSignals(charger: ChargerView): { label: string; tone: TagTone }[] {
  const out: { label: string; tone: TagTone }[] = [];
  if (charger.operational_status === 'ooc') out.push({ label: 'OOC', tone: 'bad' });
  else if (charger.operational_status === 'flagged') out.push({ label: 'Flagged', tone: 'warn' });
  if (charger.on_latest_firmware === false) out.push({ label: 'FW behind', tone: 'info' });
  if (charger.build_phase && charger.build_phase !== 'build_complete') out.push({ label: 'Building', tone: 'neutral' });
  return out;
}

export default function ChargerCard({ charger, onOpen }: { charger: ChargerView; onOpen: () => void }) {
  const r = charger.rollup;
  const name = charger.display_code || charger.vpn_ip;
  const signals = assetSignals(charger);
  const lastSeen = fmtLastSeen(charger.last_seen);
  return (
    <button
      type="button" onClick={onOpen}
      className={`text-left w-full bg-[#2a2a2a] rounded-md border border-gray-700 p-4 transition hover:border-outline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
        charger.online ? dominantAnim(charger) : 'opacity-70'
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <span
            role="img" aria-label={charger.online ? 'Online' : 'Offline'}
            className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${charger.online ? 'bg-emerald-400' : 'bg-slate-600'}`}
          />
          <span className="font-semibold text-white truncate">{name}</span>
          {!charger.is_internal_pool && (
            <Tag tone="warn" size="sm" className="flex-shrink-0">Customer</Tag>
          )}
        </div>
        {charger.goal_pct != null && (
          <span className="text-xs text-on-surface-variant flex-shrink-0">goal {charger.goal_pct}%</span>
        )}
      </div>

      <Reveal show={signals.length > 0}>
        <div className="flex flex-wrap gap-1 mt-1.5">
          {signals.map((s) => (
            <Tag key={s.label} tone={s.tone} size="sm">{s.label}</Tag>
          ))}
        </div>
      </Reveal>

      {charger.geo_label && (
        <div className="text-xs text-on-surface-variant mt-0.5 truncate">{charger.geo_label}</div>
      )}

      <div className="flex items-center gap-3 mt-3">
        <ChargeRing
          pct={r?.soc_avg ?? null} color={ringColor(charger)}
          glow={charger.online && dominantState(charger) === 'charging'} size={52}
        />
        <div className="text-xs text-on-surface-variant">
          {r?.present_count ?? 0}/{charger.slot_count || 50} batteries
        </div>
      </div>

      <div className="mt-3">
        <RollupChips rollup={r} />
      </div>

      <div className="flex items-center gap-4 mt-3 text-xs text-on-surface-variant">
        {r?.eta_hours_max != null && <span>ETA {fmtEta(r.eta_hours_max)}</span>}
        {r?.max_temp != null && <span>{Math.round(r.max_temp)}°C max</span>}
        {(r?.thermal_warnings || 0) > 0 && (
          <span className="text-amber-400">{r!.thermal_warnings} thermal</span>
        )}
        {!charger.online && <span>offline{lastSeen ? ` · seen ${lastSeen}` : ''}</span>}
      </div>
    </button>
  );
}
